import React, { useEffect, useRef, useState } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { FaMapMarkerAlt } from 'react-icons/fa';

const LocationPickerMap = ({ initialLat, initialLng, onLocationSelect, height = "300px", width = "100%" }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      setLoading(true);
      setError("");
      try {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          throw new Error("Missing Google Maps API Key.");
        }

        setOptions({
          apiKey: apiKey,
          version: "weekly",
        });

        const { Map } = await importLibrary('maps');
        const { Marker } = await importLibrary('marker');

        if (!isMounted) return;

        let centerLocation = { lat: 20.5937, lng: 78.9629 }; // Default India
        if (initialLat && initialLng && !isNaN(parseFloat(initialLat)) && !isNaN(parseFloat(initialLng))) {
            centerLocation = { lat: parseFloat(initialLat), lng: parseFloat(initialLng) };
        }

        const newMap = new Map(mapRef.current, {
          center: centerLocation,
          zoom: initialLat && initialLng ? 15 : 5,
          mapTypeControl: false,
        });
        
        setMap(newMap);

        let currentMarker = null;
        if (initialLat && initialLng && !isNaN(parseFloat(initialLat)) && !isNaN(parseFloat(initialLng))) {
          currentMarker = new Marker({
            map: newMap,
            position: centerLocation,
            draggable: true
          });
          setMarker(currentMarker);
        }

        // Add Click Listener to Map
        newMap.addListener('click', (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          
          if (!currentMarker) {
            currentMarker = new Marker({
                map: newMap,
                position: { lat, lng },
                draggable: true
            });
            setMarker(currentMarker);
          } else {
            currentMarker.setPosition({ lat, lng });
          }

          if (onLocationSelect) {
            onLocationSelect(lat, lng);
          }

          // Add drag listener
          currentMarker.addListener('dragend', (dragEvent) => {
            const dragLat = dragEvent.latLng.lat();
            const dragLng = dragEvent.latLng.lng();
            if (onLocationSelect) {
                onLocationSelect(dragLat, dragLng);
            }
          });
        });

      } catch (err) {
        console.error("Error loading Google Maps:", err);
        if (isMounted) setError(err.message || "Failed to load map.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initMap();

    return () => {
      isMounted = false;
    };
  }, []); // Run once on mount

  // Sync marker if props change externally
  useEffect(() => {
    if (map && marker && initialLat && initialLng && !isNaN(parseFloat(initialLat)) && !isNaN(parseFloat(initialLng))) {
        const newPos = { lat: parseFloat(initialLat), lng: parseFloat(initialLng) };
        marker.setPosition(newPos);
        map.panTo(newPos);
    }
  }, [initialLat, initialLng, map]);

  return (
    <div style={{ width, height, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1', marginTop: '10px' }}>
      {loading && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', zIndex: 10 }}>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading Map...</p>
        </div>
      )}
      {error && !loading && (
        <div style={{ position: 'absolute', top: 10, left: 10, right: 10, backgroundColor: '#fee2e2', color: '#ef4444', padding: '10px', borderRadius: '8px', zIndex: 10, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
          <FaMapMarkerAlt />
          {error}
        </div>
      )}
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      {!loading && !error && (
        <div style={{ position: 'absolute', bottom: 10, left: 10, backgroundColor: 'rgba(255,255,255,0.9)', padding: '5px 10px', borderRadius: '5px', fontSize: '0.8rem', color: '#334155', pointerEvents: 'none', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
          Click on the map or drag the marker to set coordinates
        </div>
      )}
    </div>
  );
};

export default LocationPickerMap;
